import { tryUseNuxtApp } from 'nuxt/app'

import { AnalyticsEvents } from '../types/events'
import { CLIENTPORTAL_DISCOVER_EVENT_TYPE, isDiscoverCatalogEvent, isElementClickLike } from '../tracking/event-policy'
import { resolvePendoActionSubAction } from '../tracking/pendo-action-policy'
import { buildTrackedRoute, hasCachedLibraryData, PAGE_HIT, resolveAppType, resolveEventTypes } from '../utils/page-hit'
import { clientPortalAnalyticsService } from '../services/clientportal-analytics.service'
import type { AnalyticsEventProperties } from '../types/properties'
import type { AnalyticsProvider } from './base.provider'

interface QueryClientLike { getQueryData: (queryKey: readonly unknown[]) => unknown }

function resolveHasLibraryData(locationId: string, properties: AnalyticsEventProperties): boolean {
  if (typeof properties.hasLibraryData === 'boolean') return properties.hasLibraryData
  try {
    const nuxtApp = tryUseNuxtApp()
    const queryClient = nuxtApp?.$queryClient as QueryClientLike | undefined
    if (!queryClient) return false
    return hasCachedLibraryData(locationId, key => queryClient.getQueryData(key))
  } catch {
    return false
  }
}

// Formats service-selected events into `/track/events` payloads for the Pendo endpoint.
export class PendoAnalyticsProvider implements AnalyticsProvider {
  name = 'PendoAnalytics'

  constructor(private readonly flavor: string) {}

  track(wireEvent: string, properties?: Record<string, unknown>): void {
    const enrichedProperties = (properties ?? {}) as AnalyticsEventProperties
    const path = (enrichedProperties.pagePath as string) ?? ''
    const locationId = enrichedProperties.locationId as string | undefined
    if (!path || !locationId) return

    const contactId = enrichedProperties.contactId as string | undefined
    const userId = enrichedProperties.userId as string | undefined
    const routeName = enrichedProperties.routeName as string | undefined
    const route = buildTrackedRoute(path, enrichedProperties.activeTab as string | undefined)
    const appType = resolveAppType(route, routeName, this.flavor)
    const { action, sub_action } = resolvePendoActionSubAction(wireEvent, enrichedProperties)

    const baseHit = {
      locationId,
      path: route,
      flavor: this.flavor,
      appType,
      action,
      sub_action,
      contactId,
      userId,
    }

    if (wireEvent === AnalyticsEvents.PAGE_VIEW) {
      const hasLibraryData = resolveHasLibraryData(locationId, enrichedProperties)
      for (const eventType of resolveEventTypes(path, routeName, this.flavor === 'gokollab', { hasLibraryData })) {
        const payload = { eventType, ...baseHit }
        clientPortalAnalyticsService.trackPageHit(payload)
      }
      return
    }

    const pendoProperties = enrichedProperties.pendoProperties
    const eventType = isDiscoverCatalogEvent(wireEvent)
      ? CLIENTPORTAL_DISCOVER_EVENT_TYPE
      : wireEvent === AnalyticsEvents.ELEMENT_CLICKED || wireEvent === AnalyticsEvents.NAVIGATION_CLICKED || isElementClickLike(wireEvent)
        ? PAGE_HIT.CLIENTPORTAL
        : wireEvent
    const payload = { eventType, ...baseHit, ...(pendoProperties ? { extraProperties: pendoProperties } : {}) }
    clientPortalAnalyticsService.trackPageHit(payload)
  }
}
